import { isEmpty } from 'lodash';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { ContainerBlock } from '~/models/blocks/container-block.model';
import {
    ContractOutboundEvents,
    CoreInboundEvents,
    CoreOutboundEvents,
    GeneralInboundEvents,
    GeneralOutboundEvents,
    InvoiceOutboundEvents,
    NavigationEvents,
    PageOutboundEvents,
    SchedulerOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import { ServiceItemScope } from '~/types/builder.types';
import {
    CompositionType,
    TBlockType,
    TFlowTag,
    TInvoiceTypes
} from '~/types/model.types';
import { getCompositionLayouts } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.utils';
import {
    getErrorMessagesList,
    IValidationBlockErrors
} from '~/utils/block.utils';
import { IContainerableBlockResponse, IMember } from '~/types/server.types';
import { ActionName } from '~/services/actions-manager/action-name';
import { getCompanyInfoLayouts } from '~/components/Builder/BuilderSidebar/ContainerSidebar/company-info-layout.utils';
import { MobilePreviewMode } from '~/stores/mobile.store';

import { ServiceSelectionItemBlock } from '~/models/blocks/service-selection-item-block.model';
import { Flow } from '~/models/flow.model';
import { InvoiceBlock } from '~/models/blocks/invoice-block.model';

import {
    AllOutboundEvents,
    BuilderAPIHandlerTypes,
    IMobileAppService
} from '~/components/Builder/Mobile/builderMobileApi.types';
import BuilderMobileCoreApiHandler from '~/components/Builder/Mobile/Entities/Core/BuilderMobileCoreApiHandler';
import { BuilderMobileContractApiHandler } from '~/components/Builder/Mobile/Entities/Contract/BuilderMobileContractApiHandler';
import BaseAbstractBuilderApi from '~/components/Builder/Mobile/Entities/BaseAbstractBuilderApi';
import { BuilderMobilePageApi } from '~/components/Builder/Mobile/Entities/Page/BuilderMobilePageApi';
import { BuilderMobileInvoiceApiHandler } from '~/components/Builder/Mobile/Entities/Invoice/BuilderMobileInvoiceApiHandler';
import { BuilderMobileSchedulerApiHandler } from '~/components/Builder/Mobile/Entities/Scheduler/BuilderMobileSchedulerApiHandler';
import { BuilderMobileReusableSFApi } from '~/components/Builder/Mobile/Entities/ReusableSF/BuilderMobileReusableSFApi';
import { FlowRouteType } from '~/utils/flow.utils';
import { ServerErrorType } from '~/utils/server-response.utils';
import { FlowMXContextValue } from '~/components/Builder/FlowMX.provider';
import { trackError } from '~/services/logger/error.utils';
import { goToFlow } from '../../Common/useFlowNavigation';
import { removeScheduleSend } from '~/components/Builder/Common/removeScheduleSend';
import { showFlowErrorsToast } from '~/components/Builder/Common/showFlowErrorsToast';
import { NavigationFunction } from '~/apps/main/App/hooks/useNavigation';
import type { IAppStates } from '~/apps/main/App/hooks/useNavigation.consts';
import type { IPendingTasksManager } from '~/types/angular.types';

export class BuilderMobileApi {
    rootStore: RootStore;
    MobileAppService: IMobileAppService;
    PendingTasksManager: IPendingTasksManager;
    navigate: NavigationFunction;
    AppStates: IAppStates;
    builderMobileCoreApi: BuilderMobileCoreApiHandler;
    eventNameToHandlerMap: Partial<
        Record<AllOutboundEvents, BaseAbstractBuilderApi>
    > = {};
    builderAPIHandler: Partial<
        Record<BuilderAPIHandlerTypes, BaseAbstractBuilderApi>
    >;
    private isInitialized: boolean = false;

    // Private static instance for singleton pattern
    private static instance: BuilderMobileApi;

    private constructor() {
        // Constructor is intentionally empty - instance is initialized in init()
    }

    public static getInstance(): BuilderMobileApi {
        if (!BuilderMobileApi.instance) {
            BuilderMobileApi.instance = new BuilderMobileApi();
        }
        return BuilderMobileApi.instance;
    }

    public static isInitialized(): boolean {
        return !!BuilderMobileApi.instance?.isInitialized;
    }

    init(
        rootStore: RootStore,
        flowMXContext: FlowMXContextValue,
        MobileAppService: IMobileAppService,
        PendingTasksManager: IPendingTasksManager,
        navigate: NavigationFunction,
        AppStates
    ) {
        if (this.isInitialized) {
            return;
        }

        this.isInitialized = true;
        this.rootStore = rootStore;
        this.MobileAppService = MobileAppService;
        this.navigate = navigate;
        this.AppStates = AppStates;
        this.PendingTasksManager = PendingTasksManager;
        this.builderMobileCoreApi = new BuilderMobileCoreApiHandler(
            rootStore,
            flowMXContext,
            MobileAppService
        );

        this.builderAPIHandler = {
            [BuilderAPIHandlerTypes.ContractHandler]:
                new BuilderMobileContractApiHandler(
                    rootStore,
                    flowMXContext,
                    MobileAppService,
                    this.builderMobileCoreApi
                ),
            [BuilderAPIHandlerTypes.InvoiceHandler]:
                new BuilderMobileInvoiceApiHandler(
                    rootStore,
                    flowMXContext,
                    MobileAppService,
                    this.builderMobileCoreApi
                ),
            [BuilderAPIHandlerTypes.ScheduleHandler]:
                new BuilderMobileSchedulerApiHandler(
                    rootStore,
                    flowMXContext,
                    MobileAppService,
                    this.builderMobileCoreApi
                ),
            [BuilderAPIHandlerTypes.PageHandler]: new BuilderMobilePageApi(
                rootStore,
                flowMXContext,
                MobileAppService,
                this.builderMobileCoreApi
            ),
            [BuilderAPIHandlerTypes.ReusableHandler]:
                new BuilderMobileReusableSFApi(
                    rootStore,
                    flowMXContext,
                    MobileAppService
                )
        };
        this.registerToEvents();
        this.initOutboundHandlers();
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.ReactLoaded,
            {}
        );
    }

    triggerOutboundEvent(eventName: AllOutboundEvents, args: any) {
        if (!this.isInitialized) {
            return;
        }

        if (this.eventNameToHandlerMap.hasOwnProperty(eventName)) {
            this.eventNameToHandlerMap?.[eventName]?.[eventName](args);
        } else {
            this[eventName](args);
        }
    }

    subscribeToGoToTab(goToTabCB: (string) => void) {
        this.MobileAppService.off(NavigationEvents.GoToTab);
        this.MobileAppService.on(
            NavigationEvents.GoToTab,
            event => {
                this[NavigationEvents.GoToTab](event, goToTabCB);
            },
            this
        );

        return () => this.MobileAppService.off(NavigationEvents.GoToTab);
    }

    subscribeToCancelScheduleSend(history: any) {
        this.MobileAppService.off(NavigationEvents.CancelScheduleSend);
        this.MobileAppService.on(
            NavigationEvents.CancelScheduleSend,
            () => {
                this[NavigationEvents.CancelScheduleSend](history);
            },
            this
        );

        return () =>
            this.MobileAppService.off(NavigationEvents.CancelScheduleSend);
    }

    subscribeToCancelScheduleSendUIOnly(history: any) {
        this.MobileAppService.off(NavigationEvents.CancelScheduleSendUIOnly);
        this.MobileAppService.on(
            NavigationEvents.CancelScheduleSendUIOnly,
            () => {
                this[NavigationEvents.CancelScheduleSendUIOnly](history);
            },
            this
        );

        return () =>
            this.MobileAppService.off(
                NavigationEvents.CancelScheduleSendUIOnly
            );
    }

    subscribeToExitPreview(exitPreviewCB: () => void) {
        this.MobileAppService.off(NavigationEvents.ExitPreview);
        this.MobileAppService.on(
            NavigationEvents.ExitPreview,
            () => {
                this[NavigationEvents.ExitPreview]({ exitPreviewCB });
            },
            this
        );

        return () => this.MobileAppService.off(NavigationEvents.ExitPreview);
    }

    subscribeToGoToPreview(goToPreviewCB: (isActiveRevision: boolean) => void) {
        this.MobileAppService.off(NavigationEvents.GoToPreview);
        this.MobileAppService.on(
            NavigationEvents.GoToPreview,
            ({ isActiveRevision }) => {
                this[NavigationEvents.GoToPreviewLoaded]({
                    isActiveRevision,
                    goToPreviewCB
                });
            },
            this
        );

        return () => this.MobileAppService.off(NavigationEvents.GoToPreview);
    }

    unsubscribe() {
        if (!this.isInitialized) {
            return;
        }

        if (this.MobileAppService?.offAll) {
            this.MobileAppService.offAll(this);
        }

        // Unsubscribe child handlers
        this.builderMobileCoreApi?.unsubscribe();
        Object.values(this.builderAPIHandler || {}).forEach(builderApiNode => {
            builderApiNode?.unsubscribe();
        });

        // Reset instance state
        this.isInitialized = false;
        this.eventNameToHandlerMap = {};
    }

    // OUTBOUND

    // * Navigation *

    [GeneralOutboundEvents.BlockSelectedNavigation]() {
        const {
            builderUiStore: { selectedPage }
        } = this.rootStore;

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.BlockSelected,
            {
                block: {
                    flow_id: selectedPage.flow_id,
                    page_id: selectedPage.page_id,
                    page_title: selectedPage.title,
                    type: 'navigation_block'
                }
            }
        );
    }

    [GeneralOutboundEvents.HBAppOpenFlow]({
        flowId,
        workspaceId
    }: {
        flowId: string;
        workspaceId?: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.HBAppOpenFlow,
            {
                flow_id: flowId,
                workspace_id: workspaceId
            }
        );
    }

    [GeneralOutboundEvents.HBAppOpenFile]({ fileId }: { fileId: string }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.HBAppOpenFile,
            {
                file_id: fileId
            }
        );
    }

    [GeneralOutboundEvents.GoToWorkspace]() {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.GoToWorkspace,
            {}
        );
    }

    [NavigationEvents.GoToPreviewLoaded]({
        isActiveRevision,
        goToPreviewCB
    }: {
        isActiveRevision?: boolean;
        goToPreviewCB: (isActiveRevision: boolean) => void;
    }) {
        goToPreviewCB(!!isActiveRevision);

        setTimeout(() => {
            this.MobileAppService.triggerAppEvent(
                NavigationEvents.GoToPreviewLoaded,
                {}
            );
        }, 500);
    }

    [NavigationEvents.ExitPreview]({
        exitPreviewCB
    }: {
        exitPreviewCB: () => void;
    }) {
        exitPreviewCB();

        setTimeout(() => {
            this.MobileAppService.triggerAppEvent(
                NavigationEvents.ExitPreview,
                {}
            );
        }, 500);
    }

    [NavigationEvents.GoToTab](
        { action, tab }: { action: string; tab?: string },
        goToTabCB: (string) => void
    ) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const nav = action ? `/${action}` : ``;
        let url = `/flow/${selectedFlow._id}${nav}`;

        if (action === 'responses' && tab === 'responses') {
            url += '?activeTab=responses';
            this.builderMobileCoreApi[CoreInboundEvents.UnselectBlock]({
                shouldNotSelectRoot: false
            });
        }
        goToTabCB(url);
        if (action === 'edit') {
            this.builderMobileCoreApi[CoreOutboundEvents.PageLoaded]();
        }
    }

    // * Creation *

    [GeneralOutboundEvents.RootCreateBlockClicked]({
        blockId,
        isTop
    }: {
        blockId: string;
        isTop: boolean;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block =
            blockId &&
            selectedFlow.draftRevision.blocksMap[blockId].getDataForServer();

        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.RootCreateBlockClicked,
            {
                block,
                isTop
            }
        );
    }

    [GeneralOutboundEvents.InnerCreateBlockClicked]({
        blockId
    }: {
        blockId: string;
    }) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const block =
            selectedFlow.draftRevision.blocksMap[blockId].getDataForServer();

        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.InnerCreateBlockClicked,
            {
                block
            }
        );
    }

    [GeneralOutboundEvents.AddParticipant]({
        workspaceId
    }: {
        workspaceId: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.AddParticipant,
            {
                workspaceId
            }
        );
    }

    [GeneralOutboundEvents.EditField]({
        fieldId,
        fieldLabel,
        hidden = null
    }: {
        fieldId: string;
        fieldLabel: string;
        hidden?: boolean;
    }) {
        this.MobileAppService.triggerAppEvent(GeneralOutboundEvents.EditField, {
            fieldId,
            fieldLabel,
            ...(hidden !== null ? { hidden: hidden } : {})
        });
    }

    [GeneralOutboundEvents.ServerError]({
        type,
        message,
        reason
    }: {
        type: ServerErrorType;
        message: string;
        reason: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.ServerError,
            {
                type,
                message,
                reason
            }
        );
    }

    // todo: merge this event with FlowValidationStatus around March 2022
    // Flow Validation Status already supports that - we had to add another
    // event to backwards compatibilty, but we must remove this soon
    [GeneralOutboundEvents.FlowValidationOnly]({
        isValid,
        shouldPublish = undefined,
        blocksErrors
    }: {
        isValid: boolean;
        shouldPublish: boolean;
        blocksErrors: IValidationBlockErrors[];
    }) {
        const { flowCtrl } = this.rootStore;
        // unselect all
        flowCtrl.selectBlock();
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.FlowValidationOnly,
            {
                isValid,
                shouldPublish,
                blocksErrors
            }
        );
    }

    [GeneralOutboundEvents.NavigateToEdit]({
        newSmartFileId = null
    }: {
        newSmartFileId?: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.NavigateToEdit,
            { newSmartFileId }
        );
    }

    [GeneralOutboundEvents.ModalLoaded]({ type }: { type: string }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.ModalLoaded,
            {
                type
            }
        );
    }

    [GeneralOutboundEvents.IsDirtyFlow]({ isDirty }: { isDirty: boolean }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.IsDirtyFlow,
            {
                isDirty
            }
        );
    }

    [GeneralOutboundEvents.OnAttachmentClick]({
        type,
        url,
        name
    }: {
        type: 'file' | 'image';
        url: string;
        name: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.OnAttachmentClick,
            {
                type,
                url,
                name
            }
        );
    }

    // @ts-ignore
    [GeneralOutboundEvents.OpenEditBlockWarning](blockType: string) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.OpenEditBlockWarning,
            {
                blockType
            }
        );
    }

    [GeneralOutboundEvents.FinishedCancelScheduleSend]({
        status
    }: {
        status: string;
    }) {
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.FinishedCancelScheduleSend,
            { status }
        );
    }

    // Inbound

    [GeneralInboundEvents.DesktopPreviewClicked]() {
        const {
            mobileStore: { setMobilePreviewMode }
        } = this.rootStore;
        setMobilePreviewMode(MobilePreviewMode.DESKTOP);
    }

    [GeneralInboundEvents.MobilePreviewClicked]() {
        const {
            mobileStore: { setMobilePreviewMode }
        } = this.rootStore;
        setMobilePreviewMode(MobilePreviewMode.MOBILE);
    }

    async [NavigationEvents.CancelScheduleSend](history: any) {
        const {
            flowService,
            flowStore,
            toastStore,
            fullstoryService,
            builderUiStore: {
                selectedFlowId,
                selectedFlow: { is_published, scheduled_to_be_sent }
            }
        } = this.rootStore;

        if (!scheduled_to_be_sent) {
            return;
        }
        try {
            await removeScheduleSend({
                history,
                flowId: selectedFlowId,
                flowStore,
                flowService,
                toastStore,
                fullstoryService
            });
            this[GeneralOutboundEvents.FinishedCancelScheduleSend]({
                status: is_published ? 'responses' : 'edit'
            });
        } catch (e) {
            this[GeneralOutboundEvents.FinishedCancelScheduleSend]({
                status: 'error'
            });
            trackError({
                error: e,
                message: 'Failed to Cancel Schedule Send',
                context: { selectedFlowId }
            });
        }
    }

    [NavigationEvents.CancelScheduleSendUIOnly](history: any) {
        const {
            builderUiStore: { selectedFlow, selectedFlowId, setShareEmail }
        } = this.rootStore;
        if (!selectedFlow.scheduled_to_be_sent) {
            return;
        }
        selectedFlow.setScheduleSend(null);
        setShareEmail({ time_to_send: null });
        if (!selectedFlow.is_published) {
            setTimeout(() => {
                goToFlow({
                    flowId: selectedFlowId,
                    history,
                    forceRoute: FlowRouteType.edit
                });
                this[GeneralOutboundEvents.NavigateToEdit]({});
            }, 2000);
        }
    }

    [GeneralInboundEvents.AddMember]({ member }: { member?: IMember }) {
        const {
            builderUiStore: { selectedFlow, selectedWorkspace }
        } = this.rootStore;
        const isMemberAlreadyExists = selectedWorkspace?.members.some(
            m => m._id === member._id
        );
        if (member && !isMemberAlreadyExists) {
            selectedWorkspace.addMember(member);
            selectedFlow.addMember(member);
        }
    }

    [GeneralInboundEvents.FinalizeSelectCompositionLayout]({
        blockId
    }: {
        blockId: string;
    }) {
        const {
            actionsManager,
            builderUiStore: { selectedPage, selectedFlow }
        } = this.rootStore;
        const block = selectedFlow.draftRevision.blocksMap[blockId];
        const action = actionsManager.getActionByIdParam({
            blockId: block.parent_id,
            pageId: selectedPage.page_id,
            actionName: ActionName.CreateCompositionBlock
        });
        actionsManager.finishMultiStageAction(action);
    }

    [GeneralInboundEvents.UpdateCompositionLayout]({
        layoutType = undefined,
        compositionType = undefined
    }: {
        blockId: string;
        layoutType: string;
        compositionType: CompositionType;
    }) {
        const {
            builderUiStore: { selectedFlow, selectedBlock }
        } = this.rootStore;

        const layout =
            layoutType &&
            (compositionType === CompositionType.CompanyInfo
                ? getCompanyInfoLayouts(selectedFlow.company).layouts[
                      layoutType
                  ].layoutStruct
                : getCompositionLayouts().layouts[layoutType].layoutStruct);

        this.rootStore.blockCtrl.updateCompositionLayout(
            selectedBlock,
            compositionType,
            layout
        );
    }

    [GeneralInboundEvents.ValidateFlow]({
        shouldPublish = undefined
    }: {
        shouldPublish?: boolean;
    }) {
        const { builderUiStore, toastStore } = this.rootStore;
        const { selectedFlow } = builderUiStore;
        // todo: in the far future remove the shouldPublish === undefined part -
        // it's only for supporting the old versions of the app since if the won't
        // update the app it'd crash
        if (shouldPublish || shouldPublish === undefined) {
            showFlowErrorsToast({ builderUiStore, toastStore });
        }

        const blocksErrors: IValidationBlockErrors[] =
            this.getBlocksErrors(selectedFlow);

        const warnings = this.getWarnings(selectedFlow);

        this.builderMobileCoreApi[CoreOutboundEvents.FlowValidationStatus]({
            isValid: !selectedFlow.hasErrors,
            warnings,
            blocksErrors,
            shouldPublish
        });
    }

    async [GeneralInboundEvents.MergeFlowRevisions]() {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl
        } = this.rootStore;
        await flowCtrl.mergeFlowRevisions(selectedFlow._id);

        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.MergeFlowRevisions,
            {
                isValid: !selectedFlow.hasErrors
            }
        );
    }

    publish() {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl
        } = this.rootStore;
        // unselect all
        flowCtrl.selectBlock();
        // todo: remove isDirty from publishFlow once enough mobile users updated
        flowCtrl.publishFlow(selectedFlow._id).then(() => {
            this.MobileAppService.triggerAppEvent(
                GeneralOutboundEvents.PublishFlow,
                {
                    isDirty: false,
                    publishType: 'flowPublished'
                }
            );
        });
    }

    [GeneralInboundEvents.PublishAfterShare]() {
        this.publish();
    }

    async [GeneralInboundEvents.PublishFlow]() {
        const {
            builderUiStore: { selectedFlow },
            flowCtrl
        } = this.rootStore;

        if (!selectedFlow.is_template && !selectedFlow.is_published) {
            // then first publish
            this.publish();
        } else {
            if (selectedFlow.hasErrors) {
                return;
            }

            const response = await flowCtrl.prePublish(selectedFlow._id);

            if (response.diverged_actions?.length > 0) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.PublishFlow,
                    {
                        publishType: 'mergeAndReview'
                    }
                );
            } else if (
                selectedFlow.hasCriticalChanges &&
                selectedFlow.hasRecipients
            ) {
                this.MobileAppService.triggerAppEvent(
                    GeneralOutboundEvents.PublishFlow,
                    {
                        publishType: 'shareUpdatesEmail'
                    }
                );
            } else {
                // valid flow
                this.publish();
            }
        }
    }

    [GeneralInboundEvents.UpdateBlockColumns]({
        number
    }: {
        block: IContainerableBlockResponse;
        number: number;
    }) {
        const {
            builderUiStore: { selectedBlock }
        } = this.rootStore;

        this.rootStore.blockCtrl.updateBlockColumns(
            selectedBlock as ContainerBlock,
            Number(number)
        );

        this.MobileAppService.triggerAppEvent(
            CoreOutboundEvents.UpdateBlockData,
            {
                block: selectedBlock.getDataForServer()
            }
        );
    }

    // TODO: Change event name to GetBlockTemplates
    [GeneralInboundEvents.GetTemplate]({ blockType }: { blockType: TFlowTag }) {
        const {
            companyStore: { currCompany }
        } = this.rootStore;

        const templates = currCompany.getBlockTemplatesByType(blockType);
        this.MobileAppService.triggerAppEvent(
            GeneralOutboundEvents.GetTemplate,
            {
                templates
            }
        );
    }

    // TODO: Change event name to ApplyBlockTemplate
    [GeneralInboundEvents.ApplyTemplate]({
        templateId,
        blockType
    }: {
        templateId: string;
        blockType: TBlockType;
    }) {
        const {
            blockCtrl,
            builderUiStore: {
                selectedBlock,
                resetOverlays,
                selectBlockTemplate
            },
            companyStore: { currCompany }
        } = this.rootStore;

        if (templateId) {
            // chose existing template
            const template = currCompany.getBlockTemplate(templateId);
            selectBlockTemplate(template);
            blockCtrl.replaceBlockWithTemplate(
                templateId,
                selectedBlock,
                blockType
            );
        } else {
            // start from blank
            if (blockType === 'invoice_container_block') {
                const invoiceBlock: InvoiceBlock =
                    selectedBlock as InvoiceBlock;
                blockCtrl.createBlankInvoice(
                    TInvoiceTypes.PaymentSchedule,
                    invoiceBlock,
                    'invoice_container_block'
                );
            }
            if (blockType === 'contract_block') {
                blockCtrl.replaceBlockWithBlock(selectedBlock, blockType);
            }
        }

        resetOverlays();
    }

    [GeneralInboundEvents.CloseModal]() {
        const {
            builderUiStore: { setIsMobileModalOpen }
        } = this.rootStore;

        setIsMobileModalOpen(false);

        setTimeout(() => {
            this.MobileAppService.triggerAppEvent(
                GeneralOutboundEvents.CloseModal,
                {}
            );
        }, 0);
    }

    [GeneralInboundEvents.EditActions]({ action }: { action: string }) {
        const {
            actionsManager,
            flowCtrl,
            builderUiStore: { selectedFlow },
            actionsManager: {
                actionsStack: { isUndoAvailable, isRedoAvailable }
            }
        } = this.rootStore;

        if (action === 'undo') {
            if (isUndoAvailable) {
                actionsManager.undoAction();
                this.builderMobileCoreApi[CoreOutboundEvents.BlockUnselected]();
            }
        } else if (action === 'redo') {
            if (isRedoAvailable) {
                actionsManager.redoAction();
                this.builderMobileCoreApi[CoreOutboundEvents.BlockUnselected]();
            }
        } else if (action === 'clear') {
            flowCtrl.discardDraft(selectedFlow._id).then(() => {
                this.builderMobileCoreApi[CoreOutboundEvents.BlockUnselected]();
            });
        }
    }

    [GeneralInboundEvents.CustomizeFlowAfterOnboarding]() {
        const { toastStore } = this.rootStore;
        toastStore.add({
            type: ToastType.Info,
            text: 'Your personalized template includes some of the actions you told us you need to work with clients. Customize and make it your own!'
        });
    }

    [GeneralInboundEvents.StartOnboarding]() {
        const {
            mobileStore: { setIsOnboardingPreview }
        } = this.rootStore;
        setIsOnboardingPreview(true);
    }

    [GeneralInboundEvents.GoToRoot]() {
        this.navigate({
            route: this.AppStates.root_core_navigation_home
        });
    }

    // Private methods

    private registerToEvents() {
        Object.values(GeneralInboundEvents).forEach(inboundEvent => {
            this.MobileAppService?.on(
                inboundEvent,
                event => {
                    this[inboundEvent](event);
                },
                this
            );
        });
    }

    private initOutboundHandlers() {
        Object.values(CoreOutboundEvents).forEach(event => {
            this.eventNameToHandlerMap[event] = this.builderMobileCoreApi;
        });
        Object.values(InvoiceOutboundEvents).forEach(event => {
            this.eventNameToHandlerMap[event] =
                this.builderAPIHandler[BuilderAPIHandlerTypes.InvoiceHandler];
        });
        Object.values(SchedulerOutboundEvents).forEach(event => {
            this.eventNameToHandlerMap[event] =
                this.builderAPIHandler[BuilderAPIHandlerTypes.ScheduleHandler];
        });
        Object.values(ContractOutboundEvents).forEach(event => {
            this.eventNameToHandlerMap[event] =
                this.builderAPIHandler[BuilderAPIHandlerTypes.ContractHandler];
        });
        Object.values(PageOutboundEvents).forEach(event => {
            this.eventNameToHandlerMap[event] =
                this.builderAPIHandler[BuilderAPIHandlerTypes.PageHandler];
        });
    }

    // this is in infrastructure for https://honeybook.atlassian.net/browse/MOBILE-3155
    private getBlocksErrors(selectedFlow: Flow): IValidationBlockErrors[] {
        return selectedFlow.blocks
            .map(block => {
                let blockErrors = [];
                const scope = block.isInsideInvoiceBlock
                    ? ServiceItemScope.Invoice
                    : ServiceItemScope.ServiceSelection;
                const shouldShowError =
                    block instanceof ServiceSelectionItemBlock
                        ? !isEmpty(
                              block.getErrors({
                                  isDontCountSubItemsErrors: true,
                                  scope
                              })
                          )
                        : !isEmpty(block.errors);
                if (shouldShowError) {
                    blockErrors = getErrorMessagesList(block, {
                        isDontCountSubItemsErrors: false,
                        scope
                    });
                }
                return {
                    blockId: block.block_id,
                    blockErrors
                };
            })
            .filter(block => {
                return !isEmpty(block.blockErrors);
            });
    }

    private getWarnings(selectedFlow: Flow) {
        const { scheduleContainerBlock, paymentScheduleBlock } = selectedFlow;

        const warnings: string[] = [];

        if (scheduleContainerBlock) {
            if (
                scheduleContainerBlock.isDefault &&
                scheduleContainerBlock.isRequireToPayWithoutPendingMAP &&
                paymentScheduleBlock?.hasPendingMAPPayments
            ) {
                warnings.push('schedulerDefaultRequireToPayPendingMarkAsPaid');
            }
            if (
                scheduleContainerBlock.sessionConfig &&
                !scheduleContainerBlock.sessionConfig.enabled
            ) {
                warnings.push('schedulerSessionDisabled');
            }
        }

        return warnings;
    }
}

export const builderMobileApi = BuilderMobileApi.getInstance();
